import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useEmployeeSession } from "@/hooks/useEmployeeSession";
import { useMonteurT } from "@/i18n/monteur";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, BellRing, ChevronRight, CheckCircle2, X } from "lucide-react";

/**
 * KARTEN-HOME · Teil 1 (Juni 2026, betreibergenehmigt) — die Seite ist von
 * der früheren Kombi-Startseite zur reinen Unterweisungsliste geschrumpft:
 * Brand-Header, Welt-Suche und die Funktionskarten (Aushang, Hautschutzplan,
 * Führerschein) leben jetzt auf dem Karten-Home (MonteurHome.jsx).
 * Das "Gesehen"-Setzen (markTasksSeen) bleibt bewusst HIER verankert.
 */
export default function MonteurAufgaben() {
  const navigate = useNavigate();
  const { session } = useEmployeeSession();
  const t = useMonteurT();
  const [assignments, setAssignments] = useState([]);
  // Neu-seit-letztem-Besuch (Juni 2026): Banner ist pro Ansicht wegklickbar.
  const [newBannerDismissed, setNewBannerDismissed] = useState(false);

  useEffect(() => {
    // AUTH-SICHERHEIT V2 · Phase 2: Session-Marker ist employee.id.
    if (!session?.employee?.id) return;
    api
      .listMyAssignments()
      .then((rows) => {
        setAssignments(rows);
        // Neu-seit-letztem-Besuch: NACH dem Anzeigen als gesehen markieren
        // (idempotent; bewusst nicht im GET, s. Backend-Kommentar in me.py).
        api.markTasksSeen().catch(() => {});
      })
      .catch(() => {});
  }, [session]);

  const offene = assignments.filter((a) => a.status !== "erledigt");
  const erledigte = assignments.filter((a) => a.status === "erledigt");
  // Neu-seit-letztem-Besuch: Server markiert Zuweisungen nach last_seen_tasks_at.
  const neueCount = offene.filter((a) => a.is_new).length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="monteur-aufgaben">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate("/monteur")}
            data-testid="monteur-aufgaben-back"
            aria-label={t("tasks.backAria")}
            className="text-slate-500 hover:text-slate-900 p-2 -ml-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-lg font-bold text-slate-900">
            {t("tasks.heading")}
          </h1>
          <span className="ml-auto text-sm text-slate-500">
            {t("tasks.openCount", { count: offene.length })}
          </span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 py-6 space-y-8">
        {/* Neu-seit-letztem-Besuch (Juni 2026): wegklickbares Banner, nur wenn
            mindestens eine Zuweisung neuer ist als last_seen_tasks_at. */}
        {neueCount > 0 && !newBannerDismissed && (
          <div
            data-testid="new-tasks-banner"
            className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3.5"
          >
            <BellRing className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
            <p className="flex-1 text-sm font-medium text-sky-900">
              {t("tasks.newBanner", { count: neueCount })}
            </p>
            <button
              type="button"
              onClick={() => setNewBannerDismissed(true)}
              data-testid="new-tasks-banner-dismiss"
              aria-label={t("tasks.newBannerDismissAria")}
              className="text-sky-500 hover:text-sky-800 p-1 -mr-1 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Offene Aufgaben */}
        <section>
          {offene.length === 0 ? (
            <Card className="bg-white border border-slate-200 p-6 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm text-slate-600">{t("tasks.emptyToday")}</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {offene.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/monteur/aufgabe/${a.id}`)}
                  className="w-full text-left"
                  data-testid={`monteur-task-${a.id}`}
                >
                  <Card className="bg-white border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all p-5 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-slate-900 text-base leading-snug">
                        {a.course?.title}
                      </div>
                      <div className="text-sm text-slate-500 mt-1">
                        {t("tasks.dueLabel")}{" "}
                        {a.due_date
                          ? new Date(a.due_date).toLocaleDateString("de-DE")
                          : "–"}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <StatusBadge status={a.status} />
                        {a.is_new && (
                          <Badge
                            variant="outline"
                            data-testid={`task-new-badge-${a.id}`}
                            className="rounded-md font-semibold px-2.5 py-1 text-xs border bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-100"
                          >
                            {t("tasks.newBadge")}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                  </Card>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Erledigte Aufgaben */}
        {erledigte.length > 0 && (
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">
              {t("tasks.doneHeading")}
            </h2>
            <div className="space-y-2">
              {erledigte.map((a) => (
                <Card
                  key={a.id}
                  className="bg-slate-50 border border-slate-200 p-4 flex items-center justify-between gap-4"
                  data-testid={`monteur-task-done-${a.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-700 truncate">
                      {a.course?.title}
                    </div>
                    <div className="text-xs text-slate-500">
                      {t("tasks.doneAtLabel")}{" "}
                      {a.completed_at
                        ? new Date(a.completed_at).toLocaleDateString("de-DE")
                        : "–"}
                    </div>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
